<script>
  import { isProxy, toRaw } from 'vue';
  import { handleViewportSize } from '@/lib/vue-helpers.js';
  export default {
    name: 'ProductListTopLeftBadge',
    props: {
      tags: Array,
      collectionId: String,
      badgeHideConfig: {
        type: Object,
        default: () => ({})
      }
    },
    data() {
      return {
        alt: '',
        badgeDetails: window.productBadges?.topLeftBadges,
        badgeTag: '',
        color: '',
        img: '',
        isProcessing: false,
        rank: 15,
        showBadge: false,
        text: '',
        textColor: '',
        showOverrideBadge: false
      };
    },
    computed: {
      shouldShowImage() {
        return this.img && this.img.trim() !== '';
      },
      isTextBadge() {
        return this.text && !this.shouldShowImage;
      },
      textBadgeWidthVars() {
        const badges = window.productBadges || {};
        const vars = {};
        if (badges.topLeftTextBadgeWidthMobile != null) vars['--top-left-text-badge-width-mobile'] = `${badges.topLeftTextBadgeWidthMobile}px`;
        if (badges.topLeftTextBadgeWidthDesktop != null) vars['--top-left-text-badge-width-desktop'] = `${badges.topLeftTextBadgeWidthDesktop}px`;
        return vars;
      },
      badgeAltText() {
        const shopName = window.Shopify.cdnHost.replace('/cdn', '');
        return this.alt || shopName + ' ' + 'badge image';
      }
    },
    mounted() {
      const overrideEventName = window.productBadges?.overrideEventName;
      if (overrideEventName) {
        window.addEventListener(overrideEventName, event => {
          this.showOverrideBadge = event.detail === true;
          this.setTag();
        });
        if (window.productBadges?.[overrideEventName] === true) {
          this.showOverrideBadge = true;
        }
      }
      this.handleViewportSize = handleViewportSize(this);
      this.setTag();
    },
    beforeUnmount() {
      if (this.handleViewportSize) this.handleViewportSize();
    },
    watch: {
      tags: function () {
        this.setTag();
      }
    },
    methods: {
      reset() {
        this.rank = 15;
        this.showBadge = false;
        this.color = '';
        this.img = '';
        this.text = '';
        this.textColor = '';
        this.alt = '';
        this.badgeTag = '';
      },
      /**
       * Check if a badge should be hidden for the current collection
       * @param {string} badgeKey - Specific badge identifier
       * @returns {boolean} True if badge should be hidden
       */
      isHidden(badgeKey) {
        if (!this.badgeHideConfig?.topLeft || !this.collectionId) return false;
        const badgeSet = this.badgeHideConfig.topLeft[badgeKey];
        if (!badgeSet) return false;
        const normalizedCollectionId = this.collectionId.replace(/[\s_-]/g, '');
        return badgeSet.has(normalizedCollectionId);
      },
      setTag() {
        this.reset();
        if (this.isProcessing) {
          return;
        }
        this.isProcessing = true;
        requestAnimationFrame(() => {
          this.performTagUpdate();
          this.isProcessing = false;
        });
      },
      shouldShowOverrideImage(badgeData) {
        if (this.showOverrideBadge === true && badgeData.img_override) {
          return badgeData.img_override;
        }
        return badgeData.img;
      },
      shouldShowOverrideText(badgeData) {
        if (this.showOverrideBadge === true && badgeData.text_override) {
          return badgeData.text_override;
        }
        return badgeData.text;
      },
      shouldShowOverrideTextColor(badgeData) {
        if (this.showOverrideBadge === true && badgeData.text_color_override) {
          return badgeData.text_color_override;
        }
        return badgeData.text_color;
      },
      shouldShowOverrideColor(badgeData) {
        if (this.showOverrideBadge === true && badgeData.color_override) {
          return badgeData.color_override;
        }
        return badgeData.color;
      },
      performTagUpdate() {
        let badges = this.badgeDetails;
        if (isProxy(badges)) {
          badges = toRaw(badges);
        }
        let tags = this.tags;
        if (isProxy(tags)) {
          tags = toRaw(tags);
        }
        const matchingBadges = [];
        if (tags && Array.isArray(tags)) {
          for (const key in badges) {
            const badgeTag = badges[key]['tag'];
            const found = tags.find(element => element == badgeTag);
            if (found) {
              matchingBadges.push({
                tag: badgeTag,
                rank: badges[key]['rank'],
                color: this.shouldShowOverrideColor(badges[key]),
                img: this.shouldShowOverrideImage(badges[key]),
                text: this.shouldShowOverrideText(badges[key]),
                textColor: this.shouldShowOverrideTextColor(badges[key]),
                alt: badges[key]['alt'] || ''
              });
            }
          }
        }
        if (matchingBadges.length === 0) {
          this.reset();
          return;
        }
        matchingBadges.sort((a, b) => a.rank - b.rank);
        for (const badge of matchingBadges) {
          let badgeKey = null;
          if (badge.rank >= 1 && badge.rank <= 6) {
            badgeKey = `product_badge_image_tag_${badge.rank}`;
          } else if (badge.rank >= 7 && badge.rank <= 9) {
            badgeKey = `product_badge_image_text_tag_${badge.rank - 6}`;
          }
          const shouldHideThisBadge = badgeKey ? this.isHidden(badgeKey) : false;
          if (shouldHideThisBadge) {
            this.reset();
            return;
          }
          if (badge.img || badge.text) {
            this.rank = badge.rank;
            this.showBadge = true;
            this.color = badge.color;
            this.img = badge.img;
            this.text = badge.text;
            this.textColor = badge.textColor;
            this.badgeTag = badge.tag;
            this.alt = badge.alt;
            return;
          }
        }
        this.reset();
      }
    }
  };
</script>

<template>
  <div v-if="showBadge && (img || text)" class="topLeftBadge pdp-badge gt-badge" :class="{ 'topLeftBadge--text': isTextBadge }" :style="isTextBadge ? textBadgeWidthVars : null" :data-badge-tag="badgeTag">
    <img v-if="shouldShowImage" :src="img" class="badge-image" :alt="badgeAltText" />
    <div v-if="text" v-html="text" :style="{ color: textColor, backgroundColor: color }"></div>
  </div>
</template>

<style lang="scss">
  .topLeftBadge {
    position: absolute;
    top: calc(var(--inner) / 4);
    left: calc(var(--inner) / 4);
    z-index: var(--z-card-overlay);
    width: 80px;
    text-align: center;

    // safari specific styles
    -webkit-transform: translateZ(0);
    transform: translateZ(0);
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
    -webkit-perspective: 1000;
    perspective: 1000;
    will-change: opacity, transform;

    .is-tablet &,
    .is-desktop & {
      top: calc(var(--inner) / 2);
      left: calc(var(--inner) / 2);
    }

    &.topLeftBadge--text {
      width: var(--top-left-text-badge-width-mobile);

      .is-tablet &,
      .is-desktop & {
        width: var(--top-left-text-badge-width-desktop);
      }
    }

    img {
      width: 100%;
      -webkit-transform: translateZ(0);
      transform: translateZ(0);

      &.hide {
        display: none;
      }

      .is-mobile & {
        width: 90%;
      }
    }

    div {
      padding-top: 3px;
      padding-bottom: 3px;
      padding-left: 5px;
      padding-right: 5px;
      // safari specific styles
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }
  }
</style>
